import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import {
  AppStoreSubscriptionPricingRequest,
  AppStoreSubscriptionPricingResponse,
  AppStoreSubscriptionsCreateRequest,
  AppStoreSubscriptionsCreateResponse,
  AppStoreSubscriptionsValidationResponse,
  GooglePlaySubscriptionsCreateRequest,
  GooglePlaySubscriptionsCreateResponse,
  GooglePlaySubscriptionsValidationResponse,
  SubscriptionPricePointOption,
  StoredAppStoreSubscriptionRecord,
  StoredGooglePlaySubscriptionRecord,
} from "../types/subscription";
import { toAppStoreConnectLocaleCode } from "../utils/storeLocales";

const APP_STORE_SUBSCRIPTION_RECORDS_COLLECTION = "appStoreSubscriptionRecords";
const GOOGLE_PLAY_SUBSCRIPTION_RECORDS_COLLECTION = "googlePlaySubscriptionRecords";
const VALIDATE_APP_STORE_CONNECT_SUBSCRIPTIONS_ENDPOINT =
  process.env.REACT_APP_VALIDATE_APP_STORE_CONNECT_SUBSCRIPTIONS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateAppStoreConnectSubscriptions";
const CREATE_APP_STORE_CONNECT_SUBSCRIPTIONS_ENDPOINT =
  process.env.REACT_APP_CREATE_APP_STORE_CONNECT_SUBSCRIPTIONS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createAppStoreConnectSubscriptions";
const LOAD_APP_STORE_CONNECT_SUBSCRIPTION_PRICE_POINTS_ENDPOINT =
  process.env.REACT_APP_LOAD_APP_STORE_CONNECT_SUBSCRIPTION_PRICE_POINTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/loadAppStoreConnectSubscriptionPricePoints";
const CONFIGURE_APP_STORE_CONNECT_SUBSCRIPTION_PRICING_ENDPOINT =
  process.env.REACT_APP_CONFIGURE_APP_STORE_CONNECT_SUBSCRIPTION_PRICING_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/configureAppStoreConnectSubscriptionPricing";
const VALIDATE_GOOGLE_PLAY_SUBSCRIPTIONS_ENDPOINT =
  process.env.REACT_APP_VALIDATE_GOOGLE_PLAY_SUBSCRIPTIONS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateGooglePlaySubscriptions";
const CREATE_GOOGLE_PLAY_SUBSCRIPTIONS_ENDPOINT =
  process.env.REACT_APP_CREATE_GOOGLE_PLAY_SUBSCRIPTIONS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createGooglePlaySubscriptions";

class SubscriptionService {
  private normalizeAppStoreSubscriptionsRequest(
    request: AppStoreSubscriptionsCreateRequest,
  ): AppStoreSubscriptionsCreateRequest {
    return {
      ...request,
      groupLocalizations: (Array.isArray(request.groupLocalizations)
        ? request.groupLocalizations
        : []
      ).map((entry) => ({
        ...entry,
        locale: toAppStoreConnectLocaleCode(entry.locale),
      })),
      subscriptions: (Array.isArray(request.subscriptions)
        ? request.subscriptions
        : []
      ).map((subscription) => ({
        ...subscription,
        localizations: (Array.isArray(subscription.localizations)
          ? subscription.localizations
          : []
        ).map((entry) => ({
          ...entry,
          locale: toAppStoreConnectLocaleCode(entry.locale),
        })),
      })),
    };
  }

  private convertTimestampToDate(value: any): Date {
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private mapStoredRecord(id: string, data: any): StoredAppStoreSubscriptionRecord {
    return {
      recordId: id,
      userId: String(data?.userId || "").trim(),
      appId: String(data?.appId || "").trim(),
      appName: String(data?.appName || "").trim(),
      bundleId: String(data?.bundleId || "").trim(),
      appStoreConnectAppId: String(data?.appStoreConnectAppId || "").trim(),
      pricingPhaseStatus:
        String(data?.pricingPhaseStatus || "pending").trim() === "configured"
          ? "configured"
          : "pending",
      workflowPhase:
        String(data?.workflowPhase || "structure_created").trim() ===
        "pricing_configured"
          ? "pricing_configured"
          : "structure_created",
      group: {
        id: String(data?.group?.id || "").trim(),
        referenceName: String(data?.group?.referenceName || "").trim(),
        localizations: Array.isArray(data?.group?.localizations)
          ? data.group.localizations.map((entry: any) => ({
              id: String(entry?.id || "").trim(),
              locale: String(entry?.locale || "").trim(),
              name: String(entry?.name || "").trim(),
            }))
          : [],
      },
      subscriptions: Array.isArray(data?.subscriptions)
        ? data.subscriptions.map((entry: any) => ({
            id: String(entry?.id || "").trim(),
            productId: String(entry?.productId || "").trim(),
            referenceName: String(entry?.referenceName || "").trim(),
            name: String(entry?.name || "").trim(),
            subscriptionPeriod: String(entry?.subscriptionPeriod || "ONE_MONTH").trim() as any,
            familySharable: Boolean(entry?.familySharable),
            groupLevel:
              typeof entry?.groupLevel === "number" ? entry.groupLevel : null,
            localizations: Array.isArray(entry?.localizations)
              ? entry.localizations.map((localization: any) => ({
                  id: String(localization?.id || "").trim(),
                  locale: String(localization?.locale || "").trim(),
                  name: String(localization?.name || "").trim(),
                }))
              : [],
          }))
        : [],
      createdAt: this.convertTimestampToDate(data?.createdAt),
      updatedAt: this.convertTimestampToDate(data?.updatedAt),
    };
  }

  private mapStoredGooglePlayRecord(
    id: string,
    data: any,
  ): StoredGooglePlaySubscriptionRecord {
    return {
      recordId: id,
      userId: String(data?.userId || "").trim(),
      appId: String(data?.appId || "").trim(),
      appName: String(data?.appName || "").trim(),
      packageName: String(data?.packageName || "").trim(),
      pricingPhaseStatus: "configured",
      workflowPhase: "pricing_configured",
      subscriptions: Array.isArray(data?.subscriptions)
        ? data.subscriptions.map((entry: any) => ({
            id: String(entry?.id || "").trim(),
            productId: String(entry?.productId || "").trim(),
            referenceName: String(entry?.referenceName || "").trim(),
            subscriptionPeriod: String(
              entry?.subscriptionPeriod || "ONE_MONTH",
            ).trim() as any,
            basePlanId: String(entry?.basePlanId || "").trim(),
            currencyCode: String(entry?.currencyCode || "USD").trim(),
            priceAmount: String(entry?.priceAmount || "").trim(),
            localizations: Array.isArray(entry?.localizations)
              ? entry.localizations.map((localization: any) => ({
                  locale: String(localization?.locale || "").trim(),
                  title: String(localization?.title || "").trim(),
                  description: String(localization?.description || "").trim(),
                  benefits: Array.isArray(localization?.benefits)
                    ? localization.benefits
                        .map((benefit: any) => String(benefit || "").trim())
                        .filter(Boolean)
                    : [],
                }))
              : [],
            status: String(entry?.status || "created").trim() === "skipped"
              ? "skipped"
              : "created",
            message: String(entry?.message || "").trim(),
          }))
        : [],
      createdAt: this.convertTimestampToDate(data?.createdAt),
      updatedAt: this.convertTimestampToDate(data?.updatedAt),
    };
  }

  private async postAuthenticatedJson<T>(
    url: string,
    body: unknown,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  async validateAppStoreConnectSubscriptions(
    request: AppStoreSubscriptionsCreateRequest,
  ): Promise<AppStoreSubscriptionsValidationResponse> {
    const normalizedRequest =
      this.normalizeAppStoreSubscriptionsRequest(request);
    return this.postAuthenticatedJson<AppStoreSubscriptionsValidationResponse>(
      VALIDATE_APP_STORE_CONNECT_SUBSCRIPTIONS_ENDPOINT,
      normalizedRequest,
    );
  }

  async createAppStoreConnectSubscriptions(
    request: AppStoreSubscriptionsCreateRequest,
  ): Promise<AppStoreSubscriptionsCreateResponse> {
    const normalizedRequest =
      this.normalizeAppStoreSubscriptionsRequest(request);
    return this.postAuthenticatedJson<AppStoreSubscriptionsCreateResponse>(
      CREATE_APP_STORE_CONNECT_SUBSCRIPTIONS_ENDPOINT,
      normalizedRequest,
    );
  }

  async validateGooglePlaySubscriptions(
    request: GooglePlaySubscriptionsCreateRequest,
  ): Promise<GooglePlaySubscriptionsValidationResponse> {
    return this.postAuthenticatedJson<GooglePlaySubscriptionsValidationResponse>(
      VALIDATE_GOOGLE_PLAY_SUBSCRIPTIONS_ENDPOINT,
      request,
    );
  }

  async createGooglePlaySubscriptions(
    request: GooglePlaySubscriptionsCreateRequest,
  ): Promise<GooglePlaySubscriptionsCreateResponse> {
    return this.postAuthenticatedJson<GooglePlaySubscriptionsCreateResponse>(
      CREATE_GOOGLE_PLAY_SUBSCRIPTIONS_ENDPOINT,
      request,
    );
  }

  async listStoredAppSubscriptions(
    appId: string,
    userId: string,
  ): Promise<StoredAppStoreSubscriptionRecord[]> {
    const snapshot = await getDocs(
      query(
        collection(db, APP_STORE_SUBSCRIPTION_RECORDS_COLLECTION),
        where("appId", "==", String(appId || "").trim()),
        where("userId", "==", String(userId || "").trim()),
      ),
    );

    return snapshot.docs
      .map((doc) => this.mapStoredRecord(doc.id, doc.data()))
      .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime());
  }

  async deleteStoredAppSubscriptionRecord(recordId: string): Promise<void> {
    await deleteDoc(doc(db, APP_STORE_SUBSCRIPTION_RECORDS_COLLECTION, recordId));
  }

  async listStoredGooglePlaySubscriptionRecords(
    appId: string,
    userId: string,
  ): Promise<StoredGooglePlaySubscriptionRecord[]> {
    const snapshot = await getDocs(
      query(
        collection(db, GOOGLE_PLAY_SUBSCRIPTION_RECORDS_COLLECTION),
        where("appId", "==", String(appId || "").trim()),
        where("userId", "==", String(userId || "").trim()),
      ),
    );

    return snapshot.docs
      .map((doc) => this.mapStoredGooglePlayRecord(doc.id, doc.data()))
      .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime());
  }

  async deleteStoredGooglePlaySubscriptionRecord(recordId: string): Promise<void> {
    await deleteDoc(doc(db, GOOGLE_PLAY_SUBSCRIPTION_RECORDS_COLLECTION, recordId));
  }

  async loadSubscriptionPricePoints(input: {
    appId: string;
    recordId: string;
    baseTerritory: string;
  }): Promise<{
    success: boolean;
    appId: string;
    recordId: string;
    baseTerritory: string;
    pricePoints: SubscriptionPricePointOption[];
    message: string;
  }> {
    return this.postAuthenticatedJson(
      LOAD_APP_STORE_CONNECT_SUBSCRIPTION_PRICE_POINTS_ENDPOINT,
      input,
    );
  }

  async configureSubscriptionPricing(
    request: AppStoreSubscriptionPricingRequest,
  ): Promise<AppStoreSubscriptionPricingResponse> {
    return this.postAuthenticatedJson<AppStoreSubscriptionPricingResponse>(
      CONFIGURE_APP_STORE_CONNECT_SUBSCRIPTION_PRICING_ENDPOINT,
      request,
    );
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
